import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { Rol, Token, UsuarioDTO } from '../models/auth.models';
import { ErrorFrontend } from '../utils/error.utils';
import { AutorizacionService } from './autorizacion.service';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly autorizacionService = inject(AutorizacionService);

  private readonly _usuario = signal<UsuarioDTO | null>(null);

  /** Perfil en vuelo: evita pedirlo dos veces si coinciden el arranque y un guard. */
  private _perfilPromesa?: Promise<UsuarioDTO | null>;

  readonly usuario = this._usuario.asReadonly();
  readonly autenticado = computed(() => this._usuario() !== null);
  readonly rol = computed(() => this._usuario()?.rol ?? null);
  readonly esInterno = computed(() => {
    const rol = this.rol();
    return rol === Rol.ADMIN || rol === Rol.OPERADOR;
  });

  async login(username: string, password: string): Promise<void> {
    try {
      const token = await firstValueFrom(
        this.http.post<Token>('autenticacion/login/', { username, password }),
      );
      this.tokenService.guardarToken(token);
      await this.cargarPerfil();
      await this.autorizacionService.cargar();
    } catch (error) {
      this.limpiar();
      throw new ErrorFrontend(error);
    }
  }

  async cargarPerfil(): Promise<UsuarioDTO | null> {
    if (this._perfilPromesa) return this._perfilPromesa;

    this._perfilPromesa = firstValueFrom(
      this.http.get<UsuarioDTO>('autenticacion/usuarios/perfil/'),
    )
      .then((usuario) => {
        this._usuario.set(usuario);
        return usuario;
      })
      .catch(() => {
        this._usuario.set(null);
        return null;
      })
      .finally(() => {
        this._perfilPromesa = undefined;
      });

    return this._perfilPromesa;
  }

  /**
   * Restaura la sesión antes de resolver la primera ruta.
   *
   * Sin esto, al recargar la página el shell se pintaría un instante sin usuario
   * ni menú mientras el guard resuelve.
   */
  async restaurarSesion(): Promise<boolean> {
    if (await this.tokenService.refreshTokenExpirado()) {
      this.limpiar();
      return false;
    }

    const usuario = await this.cargarPerfil();
    if (!usuario) {
      this.limpiar();
      return false;
    }

    await this.autorizacionService.cargar();
    return true;
  }

  async logout(): Promise<void> {
    const refresh = this.tokenService.refreshToken();
    if (refresh) {
      await firstValueFrom(
        this.http.post('autenticacion/logout/', { refresh_token: refresh }),
      ).catch(() => undefined); // cerrar sesión local aunque el backend falle
    }
    this.limpiar();
    this.router.navigate(['/login']);
  }

  limpiar(): void {
    this._usuario.set(null);
    this._perfilPromesa = undefined;
    this.tokenService.limpiarToken();
    this.autorizacionService.limpiar();
  }
}
